import type { UstvarjanjeVrednosti as Razdelek, ZnesekVrednosti } from '@koda/shared';
import { useState, type JSX, type KeyboardEvent } from 'react';

import { decimalno, evri, znesek } from '../lib/format';
import { useBesedila } from '../lib/jezik';
import { celicaDelov, vsotaDelov } from './kazalniki-skupno';
import type { Celica } from './PanelRazclenitve';

/**
 * Mere grafa po zasnovi »Kazalniki – dashboard«: splet (812 × 330) in telefon
 * (326 × 280). Trije stolpci enake višine; vsak razdeli del prejšnjega, ki
 * teče naprej, svetel pas pa ta del poveže s celotnim naslednjim stolpcem.
 */
export interface MereGrafa {
  sirina: number;
  visina: number;
  stolpec: number;
  /** Levi rob vsakega od treh stolpcev. */
  x: [number, number, number];
  vrh: number;
  dno: number;
  /** Navpični položaj zneska nad stolpcem in naziva pod njim. */
  yZnesek: number;
  yNaziv: number;
  pisavaZnesek: number;
  pisavaNaziv: number;
  /** Na telefonu je v segmentu samo znesek, na spletu naziv in znesek. */
  telefon: boolean;
}

export const SPLET: MereGrafa = {
  sirina: 812,
  visina: 330,
  stolpec: 176,
  x: [0, 318, 636],
  vrh: 34,
  dno: 286,
  yZnesek: 24,
  yNaziv: 314,
  pisavaZnesek: 14,
  pisavaNaziv: 13,
  telefon: false,
};

export const TELEFON: MereGrafa = {
  sirina: 326,
  visina: 280,
  stolpec: 88,
  x: [0, 119, 238],
  vrh: 34,
  dno: 236,
  yZnesek: 24,
  yNaziv: 264,
  pisavaZnesek: 13,
  pisavaNaziv: 12,
  telefon: true,
};

/** Barve zasnove; rdeča je barva izgube v vsej aplikaciji. */
const BARVE = {
  blago: '#dedad2',
  modra: '#256abf',
  svetla: '#c9dcf5',
  amortizacija: '#9cc2f1',
  razvoj: '#1b4f91',
  pas: '#e7f0fb',
  izguba: '#a4262c',
} as const;
const TEMNE: string[] = [BARVE.modra, BARVE.razvoj, BARVE.izguba];

/** Segment stolpca: kateri znesek, barva in navpični obseg. */
export interface Segment {
  kljuc: keyof Pick<
    Razdelek,
    'blagoMaterialStoritve' | 'dodanaVrednost' | 'delo' | 'amortizacija' | 'ebit' | 'odplacila' | 'zaRazvoj'
  >;
  barva: string;
  y: number;
  h: number;
  /** Oznaka namesto naziva (»Primanjkljaj«, »Izguba iz poslovanja«). */
  oznaka?: 'primanjkljaj' | 'izguba';
}

export interface Geometrija {
  stolpci: { kljuc: 'kosmatiDonos' | 'dodanaVrednost' | 'ebit'; x: number; segmenti: Segment[]; nicla: number | null }[];
  /** Pasova med stolpcema: mnogokotnik kot niz točk za `<path d>`. */
  pasova: string[];
}

const r1 = (v: number): number => Math.round(v * 10) / 10;
const pot = (tocke: [number, number][]): string =>
  `M${tocke.map(([x, y]) => `${x.toFixed(1)},${y.toFixed(1)}`).join(' L')} Z`;

/**
 * Geometrija grafa ali `null`, kadar ga ni mogoče pošteno narisati (negativna
 * dodana vrednost, negativen strošek, negativna odplačila).
 *
 * Običajno: stolpec 1 dodana vrednost spodaj in blago zgoraj; stolpec 2 delo,
 * amortizacija, EBIT zgoraj; stolpec 3 odplačila zgoraj in za razvoj spodaj.
 * Primanjkljaj (odplačila > EBIT): stolpec 3 obsega odplačila; nad ničlo je
 * del, ki ga pokrije EBIT, pod njo rdeč primanjkljaj. Izguba (EBIT < 0):
 * stolpec 2 ima nad ničlo delo in amortizacijo, pod njo rdečo izgubo; tretji
 * stolpec se ne deli.
 */
export function geometrija(uv: Razdelek, m: MereGrafa): Geometrija | null {
  const n = (z: ZnesekVrednosti): number => Number(z.znesek);
  const kd = n(uv.kosmatiDonos);
  const dv = n(uv.dodanaVrednost);
  const [blago, delo, amort, ebit, odpl, razvoj] = [
    n(uv.blagoMaterialStoritve),
    n(uv.delo),
    n(uv.amortizacija),
    n(uv.ebit),
    n(uv.odplacila),
    n(uv.zaRazvoj),
  ];
  if (!(kd > 0) || !(dv > 0) || blago < 0 || delo < 0 || amort < 0 || odpl < 0) return null;
  const H = m.dno - m.vrh;
  const [x1, x2, x3] = m.x;
  const w = m.stolpec;

  // Stolpec 1: dodana vrednost spodaj, blago, material, storitve zgoraj.
  const hDv = r1((H * dv) / kd);
  const yDv = r1(m.dno - hDv);
  const s1: Segment[] = [
    { kljuc: 'dodanaVrednost', barva: BARVE.modra, y: yDv, h: hDv },
    { kljuc: 'blagoMaterialStoritve', barva: BARVE.blago, y: m.vrh, h: r1(yDv - m.vrh) },
  ];
  const pas1 = pot([
    [x1 + w, yDv],
    [x2, m.vrh],
    [x2, m.dno],
    [x1 + w, m.dno],
  ]);

  if (ebit < 0) {
    // Izguba: nad ničlo delo in amortizacija, pod njo izguba iz poslovanja.
    const obseg = delo + amort - ebit;
    const k = H / obseg;
    const hIzguba = r1(-ebit * k);
    const nicla = r1(m.dno - hIzguba);
    const hDelo = r1(delo * k);
    const yDelo = r1(nicla - hDelo);
    const s2: Segment[] = [
      { kljuc: 'delo', barva: BARVE.svetla, y: yDelo, h: hDelo },
      { kljuc: 'amortizacija', barva: BARVE.amortizacija, y: m.vrh, h: r1(yDelo - m.vrh) },
      { kljuc: 'ebit', barva: BARVE.izguba, y: nicla, h: hIzguba, oznaka: 'izguba' },
    ];
    const s3: Segment[] = [{ kljuc: 'ebit', barva: BARVE.izguba, y: m.vrh, h: H, oznaka: 'izguba' }];
    return {
      stolpci: [
        { kljuc: 'kosmatiDonos', x: x1, segmenti: s1, nicla: null },
        { kljuc: 'dodanaVrednost', x: x2, segmenti: s2, nicla },
        { kljuc: 'ebit', x: x3, segmenti: s3, nicla: null },
      ],
      pasova: [
        pas1,
        pot([
          [x2 + w, nicla],
          [x3, m.vrh],
          [x3, m.dno],
          [x2 + w, m.dno],
        ]),
      ],
    };
  }

  // Stolpec 2: delo spodaj, amortizacija, EBIT zgoraj.
  const hDelo = r1((H * delo) / dv);
  const yDelo = r1(m.dno - hDelo);
  const hEbit = r1((H * ebit) / dv);
  const yAmort = r1(m.vrh + hEbit);
  const s2: Segment[] = [
    { kljuc: 'delo', barva: BARVE.svetla, y: yDelo, h: hDelo },
    { kljuc: 'amortizacija', barva: BARVE.amortizacija, y: yAmort, h: r1(yDelo - yAmort) },
    { kljuc: 'ebit', barva: BARVE.modra, y: m.vrh, h: hEbit },
  ];
  const pas2 = (spodaj: number): string =>
    pot([
      [x2 + w, m.vrh],
      [x3, m.vrh],
      [x3, spodaj],
      [x2 + w, yAmort],
    ]);

  if (razvoj < 0) {
    // Primanjkljaj: stolpec obsega odplačila; EBIT jih pokrije do ničle.
    const k = H / odpl;
    const hPrim = r1(-razvoj * k);
    const nicla = r1(m.dno - hPrim);
    const s3: Segment[] = [
      { kljuc: 'odplacila', barva: BARVE.svetla, y: m.vrh, h: r1(nicla - m.vrh) },
      { kljuc: 'zaRazvoj', barva: BARVE.izguba, y: nicla, h: hPrim, oznaka: 'primanjkljaj' },
    ];
    return {
      stolpci: [
        { kljuc: 'kosmatiDonos', x: x1, segmenti: s1, nicla: null },
        { kljuc: 'dodanaVrednost', x: x2, segmenti: s2, nicla: null },
        { kljuc: 'ebit', x: x3, segmenti: s3, nicla },
      ],
      pasova: [pas1, pas2(nicla)],
    };
  }

  // Stolpec 3: odplačila zgoraj, za razvoj spodaj (EBIT 0: vse prazno).
  const hRazvoj = ebit > 0 ? r1((H * razvoj) / ebit) : 0;
  const yRazvoj = r1(m.dno - hRazvoj);
  const s3: Segment[] =
    ebit > 0
      ? [
          { kljuc: 'zaRazvoj', barva: BARVE.razvoj, y: yRazvoj, h: hRazvoj },
          { kljuc: 'odplacila', barva: BARVE.svetla, y: m.vrh, h: r1(yRazvoj - m.vrh) },
        ]
      : [];
  return {
    stolpci: [
      { kljuc: 'kosmatiDonos', x: x1, segmenti: s1, nicla: null },
      { kljuc: 'dodanaVrednost', x: x2, segmenti: s2, nicla: null },
      { kljuc: 'ebit', x: x3, segmenti: s3, nicla: null },
    ],
    pasova: [pas1, pas2(m.dno)],
  };
}

/** Znesek na evre brez znaka valute (v segmentu grafa). */
const brezValute = (z: string): string => evri(z).replace(/\s€$/u, '');

interface Lastnosti {
  razdelek: Razdelek;
  obdobje: string;
  odprt: boolean;
  naOdpri: () => void;
  naCelico: (celica: Celica) => void;
}

/**
 * Ustvarjanje vrednosti (6.3): graf treh stolpcev po zasnovi, trije podatki
 * pod njim, odplačila po obrestih in glavnicah, »Kaj pomeni?« in »Poglej
 * izračun«. Segment, ki je vsota knjižb, odpre razčlenitev; izpeljan
 * (odplačila, za razvoj) odpre izračun.
 */
export function UstvarjanjeVrednosti({ razdelek: uv, obdobje, odprt, naOdpri, naCelico }: Lastnosti): JSX.Element {
  const b = useBesedila();
  const [pomen, setPomen] = useState(false);
  const odpri = (z: ZnesekVrednosti): void => {
    if (z.deli.length > 0) naCelico(celicaDelov(z.naziv, obdobje, vsotaDelov(z.deli), z.deli));
    else if (!odprt) naOdpri();
  };
  const naTipko = (z: ZnesekVrednosti) => (e: KeyboardEvent): void => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      odpri(z);
    }
  };
  const nazivSegmenta = (s: Segment): string =>
    s.oznaka === 'primanjkljaj' ? b.pk.uvPrimanjkljaj : s.oznaka === 'izguba' ? b.pk.uvIzguba : uv[s.kljuc].naziv;

  const graf = (m: MereGrafa, razred: string): JSX.Element | null => {
    const g = geometrija(uv, m);
    if (g === null) return null;
    return (
      <svg
        className={`uv-graf ${razred}`}
        viewBox={`0 0 ${String(m.sirina)} ${String(m.visina)}`}
        role="img"
        aria-label={b.pk.uvOpisGrafa}
      >
        {g.pasova.map((d, i) => (
          <path key={i} d={d} fill={BARVE.pas} />
        ))}
        {g.stolpci.map((st) => {
          const cx = st.x + m.stolpec / 2;
          return (
            <g key={st.kljuc}>
              {st.segmenti.map((s) => {
                const z = uv[s.kljuc];
                const cy = s.y + s.h / 2;
                const barva = TEMNE.includes(s.barva) ? '#ffffff' : '#1c1b19';
                const vrednost = brezValute(z.znesek);
                // Dve vrstici, kadar je segment dovolj visok; sicer ena ali nič.
                const dve = !m.telefon && s.h >= 40;
                const ena = s.h >= (m.telefon ? 12 : 16);
                return (
                  <g
                    key={`${s.kljuc}-${String(s.y)}`}
                    className="uv-segment"
                    role="button"
                    tabIndex={0}
                    aria-label={`${nazivSegmenta(s)} ${evri(z.znesek)}`}
                    onClick={() => {
                      odpri(z);
                    }}
                    onKeyDown={naTipko(z)}
                  >
                    <title>{`${nazivSegmenta(s)}: ${evri(z.znesek)}`}</title>
                    <rect x={st.x} y={s.y} width={m.stolpec} height={s.h} fill={s.barva} />
                    {dve ? (
                      <>
                        <text x={cx} y={r1(cy - 3)} textAnchor="middle" fontSize="12" fill={barva}>
                          {nazivSegmenta(s)}
                        </text>
                        <text x={cx} y={r1(cy + 13)} textAnchor="middle" fontSize="13" fontWeight="600" fill={barva}>
                          {vrednost}
                        </text>
                      </>
                    ) : ena ? (
                      <text x={cx} y={r1(cy + 4)} textAnchor="middle" fontSize={m.telefon ? 11 : 12} fontWeight="600" fill={barva}>
                        {m.telefon ? vrednost : `${nazivSegmenta(s)} ${vrednost}`}
                      </text>
                    ) : null}
                  </g>
                );
              })}
              {st.nicla !== null && (
                <line x1={st.x} x2={st.x + m.stolpec} y1={st.nicla} y2={st.nicla} stroke="#1c1b19" strokeWidth="1.5" />
              )}
              <text x={cx} y={m.yZnesek} textAnchor="middle" fontSize={m.pisavaZnesek} fontWeight="600" fill="#1c1b19">
                {evri(uv[st.kljuc].znesek)}
              </text>
              <text x={cx} y={m.yNaziv} textAnchor="middle" fontSize={m.pisavaNaziv} fill="#5d5b55">
                {uv[st.kljuc].naziv}
              </text>
            </g>
          );
        })}
      </svg>
    );
  };

  const splet = graf(SPLET, 'samo-splet');
  const rg = uv.razclenitevGlavnic;
  const prenosi = rg.poTemeljnicah.length > 0;
  const primanjkljaj = uv.stanje === 'primanjkljaj';
  const izguba = uv.stanje === 'izguba';
  const podatek = (naziv: string, vrednost: string, pod: string, negativno = false): JSX.Element => (
    <div className="uv-podatek">
      <span className="uv-podatek-naziv">{naziv}</span>
      <span className={`uv-podatek-vrednost${negativno ? ' negativno' : ''}`}>{vrednost}</span>
      <span className="uv-podatek-naziv">{pod}</span>
    </div>
  );
  const odstotek = (d: string | null): string => (d === null ? b.pk.ni : `${decimalno(d, 1)} %`);

  return (
    <section id={`kazalnik-${uv.koda}`} className="ustvarjanje-vrednosti" aria-labelledby="naslov-ustvarjanje-vrednosti">
      <div className="uv-glava">
        <h2 id="naslov-ustvarjanje-vrednosti">{uv.naziv}</h2>
        <div className="uv-podnaslov samo-splet">{b.pk.uvPodnaslov(obdobje)}</div>
        <div className="uv-podnaslov samo-telefon">{b.pk.uvPodnaslovKratek}</div>
      </div>
      {splet === null ? (
        <p className="uv-brez-grafa">{b.pk.uvBrezGrafa}</p>
      ) : (
        <>
          {splet}
          {graf(TELEFON, 'samo-telefon')}
          <div className="uv-legenda samo-telefon">
            {(
              [
                [BARVE.blago, b.pk.uvLegenda.blago],
                [BARVE.modra, b.pk.uvLegenda.dodana],
                [BARVE.svetla, b.pk.uvLegenda.delo],
                [BARVE.amortizacija, b.pk.uvLegenda.amortizacija],
                [BARVE.razvoj, b.pk.uvLegenda.razvoj],
              ] as const
            ).map(([barva, naziv]) => (
              <span key={naziv}>
                <span className="uv-kvadrat" style={{ background: barva }} />
                {naziv}
              </span>
            ))}
          </div>
        </>
      )}
      <div className="uv-podatki samo-splet">
        {podatek(uv.dodanaVrednost.naziv, odstotek(uv.delezDodaneVrednosti), b.pk.uvKosmategaDonosa)}
        {podatek(uv.delo.naziv, odstotek(uv.delezDela), b.pk.uvDodaneVrednosti)}
        {izguba
          ? podatek(uv.ebit.naziv, evri(uv.ebit.znesek), b.pk.uvIzguba.toLocaleLowerCase(), true)
          : podatek(
              uv.zaRazvoj.naziv,
              evri(uv.zaRazvoj.znesek),
              primanjkljaj
                ? `${b.pk.uvPrimanjkljaj.toLocaleLowerCase()} · ${b.pk.uvDelezDodane(odstotek(uv.delezZaRazvoj))}`
                : b.pk.uvDelezDodane(odstotek(uv.delezZaRazvoj)),
              primanjkljaj,
            )}
      </div>
      <div className="uv-stavek samo-telefon">
        {izguba
          ? b.pk.uvStavekIzguba(evri(uv.ebit.znesek))
          : b.pk.uvStavek(evri(uv.zaRazvoj.znesek), odstotek(uv.delezZaRazvoj), evri(uv.odplacila.znesek), primanjkljaj)}
      </div>
      <div className="uv-noga">
        <span className="uv-odplacila">
          {b.pk.uvOdplacila(evri(uv.odplacila.znesek), evri(uv.obresti.znesek), evri(uv.glavnice.znesek))}
        </span>
        <span className="uv-povezave">
          <button
            type="button"
            className="povezava-gumb"
            aria-expanded={pomen}
            onClick={() => {
              setPomen(!pomen);
            }}
          >
            {b.pk.kajPomeni}
          </button>
          <button type="button" className="povezava-gumb" aria-expanded={odprt} onClick={naOdpri}>
            {odprt ? b.pk.skrijIzracun : b.pk.pogledIzracun}
          </button>
        </span>
      </div>
      {pomen && <p className="razlaga-kazalnika">{uv.opis}</p>}
      {odprt && (
        <div className="izracun-kazalnika">
          <p className="drobno">{uv.formula}</p>
          <table className="sestavine-izracuna">
            <thead>
              <tr>
                <th>{b.pk.nsPostavka}</th>
                <th className="stevilka">{obdobje}</th>
                <th>{b.pk.uvIzracun}</th>
              </tr>
            </thead>
            <tbody>
              {(
                [
                  uv.kosmatiDonos,
                  uv.blagoMaterialStoritve,
                  uv.dodanaVrednost,
                  uv.delo,
                  uv.amortizacija,
                  uv.ebit,
                  uv.obresti,
                  // Sestavine glavnic, kadar so izpeljane: promet v breme
                  // (knjižbe), prenosi in prenos vsake temeljnice (brez knjižb).
                  ...(prenosi
                    ? [
                        rg.prometVBreme,
                        rg.prenosi,
                        ...rg.poTemeljnicah.map(
                          (t): ZnesekVrednosti & { podrejena: true } => ({
                            naziv: b.pk.uvTemeljnica(t.temeljnica),
                            znesek: `-${t.znesek}`,
                            izracun: null,
                            deli: [],
                            podrejena: true,
                          }),
                        ),
                      ]
                    : []),
                  uv.glavnice,
                  uv.odplacila,
                  uv.zaRazvoj,
                ] as (ZnesekVrednosti & { podrejena?: true })[]
              ).map((z) => (
                <tr key={z.naziv} className={z.podrejena === true ? 'podrejena' : undefined}>
                  <td className="postavka">{z.naziv}</td>
                  <td className="stevilka">
                    {z.deli.length === 0 ? (
                      <span>{znesek(z.znesek)}</span>
                    ) : (
                      <button
                        type="button"
                        className="znesek"
                        onClick={() => {
                          odpri(z);
                        }}
                      >
                        {znesek(z.znesek)}
                      </button>
                    )}
                  </td>
                  <td className="drobno">{z.izracun ?? ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {prenosi && <p className="drobno">{b.pk.uvPrenosi}</p>}
          {rg.neprepoznaniPrenosi !== null && (
            <p className="drobno opozorilo-glavnic">{b.pk.uvNeprepoznaniPrenosi(evri(rg.neprepoznaniPrenosi))}</p>
          )}
        </div>
      )}
    </section>
  );
}
